import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useFormik } from "formik";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { toast } from "sonner";

import useConfig from "../../../hooks/useConfig";
import HttpService from "../../../hooks/Https-services";
import PageRoutes, { APIRoutes } from "../../../utils/constants";
import useCommonDetails from "../../../Common/Hooks/useCommonDetails";
import { TIER_META, tierLabel } from "../tierMeta";
import { useGiftOptions } from "../api";
import type { BillingCycle, GiftStep, StepInfo } from "../types";

const EMAIL_RE = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;

// Payment can hop off to Stripe for a 3-D Secure challenge, which reloads the
// page on return and wipes the in-memory wizard state. We stash just enough to
// rebuild the confirmation screen under this key before the redirect.
const GIFT_RESUME_KEY = "htp_gift_payment_resume";

/**
 * Owns every piece of state, derived value, and side effect for the gift
 * wizard. Pulled out of the page component so each step's markup can live in
 * its own file without dragging this logic along - the step components only
 * receive the slice of this they need as props.
 */
export function useGiftWizard() {
  const configData = useConfig();
  const navigate = useNavigate();
  const currentUser = useCommonDetails();

  const [step, setStep] = useState<GiftStep>(1);
  const [isLoadingData, setIsLoading] = useState(false);
  const [billing, setBilling] = useState<BillingCycle>("annual");
  const [selectedPlan, setSelectedPlan] = useState<string>("insider");
  const [termCount, setTermCount] = useState<number>(1);
  const [recipientHasSub, setRecipientHasSub] = useState(false);

  // Cached via react-query so revisiting /buy-gift doesn't refetch every mount.
  const giftOptionsQuery = useGiftOptions();
  const { refetch: refetchGiftOptions } = giftOptionsQuery;
  const giftOptions = giftOptionsQuery.data?.options ?? [];
  const termMax = giftOptionsQuery.data?.termMax ?? 24;
  const pricesReady = giftOptionsQuery.isSuccess;
  const pricesError = giftOptionsQuery.isError;
  const loadOptions = useCallback(() => {
    refetchGiftOptions();
  }, [refetchGiftOptions]);

  // The gift PaymentIntent client secret, created when the buyer reaches the
  // payment step. Drives the embedded Stripe card form (StripePaymentForm).
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [orderNumber, setOrderNumber] = useState<string | null>(null);
  const [orderDate, setOrderDate] = useState<string | null>(null);

  const safeTermMax = useMemo(() => {
    const parsed = Number(termMax);
    return !isNaN(parsed) && parsed > 0
      ? Math.min(Math.max(1, parsed), 48)
      : 24;
  }, [termMax]);

  const availableTiers = useMemo(() => {
    const order = ["basic", "insider"];
    const rank = (t: string) => {
      const i = order.indexOf(t);
      return i === -1 ? order.length : i;
    };
    const validOptions =
      Array.isArray(giftOptions) && giftOptions.length ? giftOptions : [];
    return Array.from(
      new Set(
        validOptions
          .map((o) => o?.tier)
          .filter((t): t is string => Boolean(t)),
      ),
    ).sort((a, b) => rank(a) - rank(b));
  }, [giftOptions]);

  const activeLabel = tierLabel(selectedPlan);

  const priceFor = useCallback(
    (tier: string, b: BillingCycle) => {
      const opts =
        Array.isArray(giftOptions) && giftOptions.length ? giftOptions : [];
      const opt = opts.find((o) => o?.tier === tier && o?.billing === b);
      const val = opt ? Number(opt.priceInCurrency) : NaN;
      return !isNaN(val) ? val / 100 : 0;
    },
    [giftOptions],
  );

  const unitPrice = priceFor(selectedPlan, billing);
  const planPrice = unitPrice * (Number(termCount) || 1);

  const getMonthlyDisplayPrice = (tier: string) => {
    const p = priceFor(tier, billing);
    if (billing === "annual") {
      return (p / 12).toFixed(2);
    }
    return p.toFixed(2);
  };

  const getAnnualDisplayPrice = (tier: string) => {
    return priceFor(tier, "annual").toFixed(2);
  };

  useEffect(() => {
    if (availableTiers.length && !availableTiers.includes(selectedPlan)) {
      setSelectedPlan(availableTiers[0]);
    }
  }, [availableTiers, selectedPlan]);

  const checkRecipientStatus = async (rawEmail: string) => {
    const email = (rawEmail || "").trim();
    const valid = EMAIL_RE.test(email);
    const isSelf =
      currentUser?.Email &&
      email.toLowerCase() === currentUser.Email.trim().toLowerCase();
    if (!valid || isSelf) {
      setRecipientHasSub(false);
      return;
    }
    try {
      const res = await HttpService.get(
        `${APIRoutes.GiftSubscriptionRecipientStatus}?email=${encodeURIComponent(email)}`,
      );
      setRecipientHasSub(Boolean(res?.data?.data?.hasActiveSubscription));
    } catch {
      setRecipientHasSub(false);
    }
  };

  const validate = (values: any) => {
    const errors: any = {};
    if (!values.name) errors.name = "Recipient's name is required";
    // Parity with the dev-v2 gift flow: a personal message is required.
    if (!values.message || !values.message.trim())
      errors.message = "A personal message is required";
    if (!values.email) {
      errors.email = "Recipient's email is required";
    } else if (!EMAIL_RE.test(values.email)) {
      errors.email = "Enter a valid email address";
    } else if (
      currentUser?.Email &&
      values.email.trim().toLowerCase() ===
        currentUser.Email.trim().toLowerCase()
    ) {
      errors.email =
        "You can't gift to your own email. Buy a plan for yourself instead.";
    }
    if (!values.send && !values.date) {
      errors.date = "Please select a delivery date";
    }
    return errors;
  };

  const formik: any = useFormik({
    initialValues: {
      name: "",
      email: "",
      send: true,
      date: dayjs().format("YYYY-MM-DD"),
      message: "",
    },
    validate,
    validateOnChange: true,
    validateOnBlur: false,
    // Payment is completed inline by the embedded Stripe form, not by a native
    // form submit, so there is nothing to do here.
    onSubmit: () => {},
  });

  const returnUrl = `${window.location.origin}${PageRoutes.GiftPage}`;

  const clearGiftResume = () => {
    try {
      sessionStorage.removeItem(GIFT_RESUME_KEY);
    } catch {
      // sessionStorage may be unavailable (private mode); ignore.
    }
  };

  // Called right before Stripe confirms. Persists what the confirmation screen
  // needs so a 3-D Secure redirect can rebuild it on return.
  const stashGiftResume = () => {
    try {
      sessionStorage.setItem(
        GIFT_RESUME_KEY,
        JSON.stringify({
          name: formik.values.name,
          email: formik.values.email,
          message: formik.values.message,
          send: formik.values.send,
          date: formik.values.date,
          selectedPlan,
          billing,
          termCount,
          orderNumber,
          orderDate,
        }),
      );
    } catch {
      // ignore
    }
  };

  // Guards against a double-click (or edit-and-resubmit) firing two
  // CreateGiftSubscription calls before isLoadingData re-renders, which would
  // mint duplicate gifts + PaymentIntents. A ref flips synchronously.
  const submittingRef = useRef(false);

  // Creates the gift + its PaymentIntent, then advances to the payment step.
  // If account credit already covers it, jumps straight to the confirmation.
  const proceedToPayment = async () => {
    if (submittingRef.current) return;
    formik.setTouched({ name: true, email: true, message: true, date: true });
    const errors = validate(formik.values);
    if (Object.keys(errors).length > 0) {
      formik.setErrors(errors);
      return;
    }
    submittingRef.current = true;
    setIsLoading(true);
    try {
      const res = await HttpService.post(APIRoutes.CreateGiftSubscription, {
        planTier: selectedPlan,
        billingModel: billing,
        termCount,
        recipientName: formik.values.name,
        recipientEmail: formik.values.email,
        // Anchor at local noon before serializing so `.toISOString()` can't
        // roll a scheduled date back a day for positive-UTC buyers (local
        // midnight -> previous-day UTC).
        deliveryDate: formik.values.date
          ? dayjs(formik.values.date)
              .hour(12)
              .minute(0)
              .second(0)
              .millisecond(0)
              .toISOString()
          : null,
        sendNow: formik.values.send,
        message: formik.values.message,
      });

      const data = res?.data?.data;
      setOrderNumber(
        data?.paymentId || `${Date.now()}`.slice(-7).padStart(7, "0"),
      );
      setOrderDate(dayjs().format("MMM D, YYYY [at] h:mm A"));

      if (data?.alreadyPaid || !data?.clientSecret) {
        toast.success(
          "Your gift is all set - it was covered by your account credit.",
        );
        setStep(4);
        return;
      }

      setClientSecret(data.clientSecret);
      setStep(3);
    } catch (error: any) {
      const data = error?.response?.data;
      if (data?.responseType === "self_gift") {
        formik.setFieldError(
          "email",
          "You can't gift to your own email. Buy a plan for yourself instead.",
        );
        toast.error(
          data?.message || "You can't gift a subscription to your own email.",
        );
      } else {
        toast.error(data?.message ?? "Something went wrong");
      }
    } finally {
      submittingRef.current = false;
      setIsLoading(false);
    }
  };

  const onPaymentSuccess = () => {
    clearGiftResume();
    setStep(4);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const onPaymentError = (message: string) => {
    toast.error(message || "Payment could not be completed. Please try again.");
  };

  // On return from a 3-D Secure redirect Stripe appends ?redirect_status.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const status = params.get("redirect_status");
    if (!status) return;
    if (status === "succeeded") {
      // Rebuild the confirmation from the stashed details.
      try {
        const raw = sessionStorage.getItem(GIFT_RESUME_KEY);
        if (raw) {
          const saved = JSON.parse(raw);
          formik.setValues({
            ...formik.values,
            name: saved.name ?? "",
            email: saved.email ?? "",
            message: saved.message ?? "",
            send: saved.send ?? true,
            date: saved.date ?? dayjs().format("YYYY-MM-DD"),
          });
          if (saved.selectedPlan) setSelectedPlan(saved.selectedPlan);
          if (saved.billing) setBilling(saved.billing);
          if (saved.termCount) setTermCount(saved.termCount);
          setOrderNumber(saved.orderNumber ?? null);
          setOrderDate(
            saved.orderDate ?? dayjs().format("MMM D, YYYY [at] h:mm A"),
          );
        }
      } catch {
        // Fall through to the confirmation regardless of stash issues.
      }
      setStep(4);
    } else {
      // Failed / cancelled challenge: surface it instead of silently landing
      // the buyer back on step 1 as if nothing happened.
      toast.error(
        "Your payment wasn't completed. Please try again to send your gift.",
      );
    }
    clearGiftResume();
    window.history.replaceState({}, "", PageRoutes.GiftPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const stepsList: StepInfo[] = [
    {
      id: 1,
      title: "Choose Gift",
      subActive: "Select plan and quantity",
      subCompleted: `${activeLabel} Plan • ${termCount} ${billing === "annual" ? (termCount > 1 ? "Years" : "Year") : termCount > 1 ? "Months" : "Month"}`,
    },
    {
      id: 2,
      title: "Recipient & Delivery",
      subActive: "Who it's for and when",
      subCompleted: `${formik.values.send ? "Send now" : "Scheduled"}`,
    },
    {
      id: 3,
      title: "Payment",
      subActive: "Review and pay",
      subCompleted: "Payment details",
    },
    {
      id: 4,
      title: "Confirmation",
      subActive: "Gift sent",
      subCompleted: "Gift sent",
    },
  ];

  const channelName = configData?.personalChannel || "NA TV";

  const buyAnotherGift = () => {
    formik.resetForm();
    setClientSecret(null);
    setOrderNumber(null);
    setOrderDate(null);
    // Clear recipient-specific state so the fresh form doesn't show a stale
    // "already subscribes / account credit" notice for an empty email.
    setRecipientHasSub(false);
    setStep(1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const goToSchedule = () => navigate(PageRoutes.UpcomingEvent);
  const goToAccount = () => navigate(PageRoutes.AccountPage);

  return {
    configData,
    channelName,
    TIER_META,
    tierLabel,

    step,
    setStep,
    isLoadingData,
    billing,
    setBilling,
    selectedPlan,
    setSelectedPlan,
    termCount,
    setTermCount,
    safeTermMax,
    availableTiers,
    recipientHasSub,
    setRecipientHasSub,
    pricesReady,
    pricesError,
    loadOptions,

    clientSecret,
    returnUrl,
    orderNumber,
    orderDate,

    activeLabel,
    planPrice,
    getMonthlyDisplayPrice,
    getAnnualDisplayPrice,

    formik,
    checkRecipientStatus,
    proceedToPayment,
    stashGiftResume,
    onPaymentSuccess,
    onPaymentError,
    stepsList,

    buyAnotherGift,
    goToSchedule,
    goToAccount,
  };
}

export type GiftWizard = ReturnType<typeof useGiftWizard>;
